import React, { useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { useAdmin } from '../../contexts/AdminContext';
import NotificationDropdown from '../notifications/NotificationDropdown';
import toast from 'react-hot-toast';
import {
  ChartBarIcon,
  UsersIcon,
  BriefcaseIcon,
  AcademicCapIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ShieldCheckIcon,
  CogIcon,
  ExclamationTriangleIcon,
  BellIcon,
  UserCircleIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ClipboardDocumentListIcon,
  CurrencyDollarIcon,
  CreditCardIcon,
  ChartPieIcon,
  EyeIcon,
  EnvelopeIcon,
  TicketIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';

interface NavItem {
  name: string;
  href: string;
  icon: React.ComponentType<any>;
  permission?: { resource: string; action: string };
  badge?: number;
}

const AdminLayout: React.FC = () => {
  const { adminUser, logout, hasPermission } = useAdmin();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation: NavItem[] = [
    {
      name: 'Dashboard',
      href: '/admin/dashboard',
      icon: ChartBarIcon,
      permission: { resource: 'dashboard', action: 'read' }
    },
    {
      name: 'User Management',
      href: '/admin/users',
      icon: UsersIcon,
      permission: { resource: 'users', action: 'read' }
    },
    {
      name: 'Job Management',
      href: '/admin/jobs',
      icon: BriefcaseIcon,
      permission: { resource: 'jobs', action: 'read' }
    },
    {
      name: 'Course Management',
      href: '/admin/courses',
      icon: AcademicCapIcon,
      permission: { resource: 'courses', action: 'read' }
    },
    {
      name: 'Verification Queue',
      href: '/admin/verifications',
      icon: CheckCircleIcon,
      permission: { resource: 'verifications', action: 'read' },
      badge: 12 // This would be dynamic from API
    },
    {
      name: 'Content Moderation',
      href: '/admin/moderation',
      icon: EyeIcon,
      permission: { resource: 'moderation', action: 'read' }
    },
    {
      name: 'Background Checks',
      href: '/admin/background-checks',
      icon: ShieldCheckIcon,
      permission: { resource: 'background_checks', action: 'read' }
    },
    {
      name: 'Subscription Plans',
      href: '/admin/subscription-plans',
      icon: CurrencyDollarIcon,
      permission: { resource: 'system', action: 'read' }
    },
    {
      name: 'Subscriptions',
      href: '/admin/subscriptions',
      icon: CreditCardIcon,
      permission: { resource: 'subscriptions', action: 'read' }
    },
    {
      name: 'Analytics',
      href: '/admin/analytics',
      icon: ChartPieIcon,
      permission: { resource: 'analytics', action: 'read' }
    },
    {
      name: 'Reports',
      href: '/admin/reports',
      icon: DocumentTextIcon,
      permission: { resource: 'reports', action: 'read' }
    },
    {
      name: 'Email Management',
      href: '/admin/emails',
      icon: EnvelopeIcon,
      permission: { resource: 'communications', action: 'read' }
    },
    {
      name: 'Support Tickets',
      href: '/admin/support',
      icon: TicketIcon,
      permission: { resource: 'support', action: 'read' },
      badge: 5 // This would be dynamic from API - number of open tickets
    },
    {
      name: 'Contact Inquiries',
      href: '/admin/contact',
      icon: ChatBubbleLeftRightIcon,
      permission: { resource: 'communications', action: 'read' },
      badge: 3 // This would be dynamic from API - number of open inquiries
    },
    {
      name: 'System Settings',
      href: '/admin/settings',
      icon: CogIcon,
      permission: { resource: 'system', action: 'read' }
    },
    {
      name: 'Admin Management',
      href: '/admin/admins',
      icon: UserCircleIcon,
      permission: { resource: 'admin_users', action: 'read' }
    }
  ];

  const handleLogout = async () => {
    try {
      await logout();
      window.location.href = '/admin/login';
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  const isActivePath = (href: string) => {
    return location.pathname === href || 
           (href !== '/admin/dashboard' && location.pathname.startsWith(href));
  };

  // Filter navigation items based on permissions
  const filteredNavigation = navigation.filter(item => {
    if (!item.permission) return true;
    return hasPermission(item.permission.resource, item.permission.action);
  });

  const Sidebar = ({ className = '' }: { className?: string }) => (
    <div className={`flex flex-col h-full ${className}`}>
      {/* Logo */}
      <div className="flex items-center justify-center h-16 px-4 bg-indigo-800">
        <div className="flex items-center">
          <ShieldCheckIcon className="h-8 w-8 text-white" />
          <span className="ml-2 text-white font-bold text-lg">Admin</span>
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-2 py-4 bg-indigo-700">
        <div className="space-y-1">
          {filteredNavigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setSidebarOpen(false)}
              className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${
                isActivePath(item.href)
                  ? 'bg-indigo-800 text-white'
                  : 'text-indigo-100 hover:bg-indigo-600 hover:text-white'
              }`}
            >
              <item.icon
                className={`mr-3 flex-shrink-0 h-5 w-5 ${
                  isActivePath(item.href) ? 'text-white' : 'text-indigo-300'
                }`}
              />
              <span className="flex-1">{item.name}</span>
              {item.badge && (
                <span className="ml-3 inline-block py-0.5 px-2 text-xs font-medium rounded-full bg-red-500 text-white">
                  {item.badge}
                </span>
              )}
            </Link>
          ))}
        </div>
      </nav>

      {/* User Info */}
      <div className="flex-shrink-0 p-4 bg-indigo-800 border-t border-indigo-600">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center">
              <span className="text-sm font-medium text-white">
                {adminUser?.name?.charAt(0) || 'A'}
              </span>
            </div>
          </div>
          <div className="ml-3 flex-1 min-w-0">
            <p className="text-sm font-medium text-white truncate">
              {adminUser?.name}
            </p>
            <p className="text-xs text-indigo-300 truncate">
              {adminUser?.role?.replace('_', ' ')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="h-screen flex overflow-hidden bg-gray-100">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 flex z-40 md:hidden">
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={() => setSidebarOpen(false)} />
          <div className="relative flex-1 flex flex-col max-w-xs w-full bg-indigo-700">
            <div className="absolute top-0 right-0 -mr-12 pt-2">
              <button
                onClick={() => setSidebarOpen(false)}
                className="ml-1 flex items-center justify-center h-10 w-10 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              >
                <XMarkIcon className="h-6 w-6 text-white" />
              </button>
            </div>
            <Sidebar />
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0">
        <div className="flex flex-col w-64">
          <Sidebar className="bg-indigo-700" />
        </div>
      </div>

      {/* Main content area */}
      <div className="flex flex-col w-0 flex-1 overflow-hidden">
        {/* Top header */}
        <header className="bg-white shadow-sm border-b border-gray-200">
          <div className="flex items-center justify-between px-4 py-4">
            {/* Mobile menu button */}
            <button
              onClick={() => setSidebarOpen(true)}
              className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500"
            >
              <Bars3Icon className="h-6 w-6" />
            </button>

            {/* Page title */}
            <div className="flex-1 md:ml-0 ml-4">
              <h1 className="text-lg font-medium text-gray-900">
                {navigation.find(item => isActivePath(item.href))?.name || 'Admin Panel'}
              </h1>
            </div>

            {/* Right side buttons */}
            <div className="flex items-center space-x-4">
              {/* Notifications */}
              <NotificationDropdown isAdmin={true} />

              {/* Profile dropdown */}
              <div className="relative">
                <button className="flex items-center text-sm text-gray-500 hover:text-gray-700">
                  <div className="h-8 w-8 bg-indigo-600 rounded-full flex items-center justify-center mr-2">
                    <span className="text-sm font-medium text-white">
                      {adminUser?.name?.charAt(0) || 'A'}
                    </span>
                  </div>
                  <span className="hidden md:block">{adminUser?.name}</span>
                </button>
              </div>

              {/* Logout button */}
              <button
                onClick={handleLogout}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4 mr-1" />
                <span className="hidden md:block">Logout</span>
              </button>
            </div>
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 relative overflow-y-auto focus:outline-none">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout; 